/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import {ResponseData} from '@dvrd/dvr-controls';

import React, {useContext, useEffect, useMemo, useState} from 'react';

import {useNavigate} from 'react-router';

import Account from '../../../models/accountModel';

import AccountStatusChecker from '../../account/pinger/statusPinger';

import {AppContext} from '../../misc/appContext';

import useRight from '../../misc/useRight';

import {sendGaEvent} from '../../../util/analytics';

import {onConnectBolOauth} from '../../../util/bolOauthUtil';

import {
    EMAILS_ROUTE,
    INVOICE_REQUESTS_ROUTE,
    PDF_LAYOUT_ROUTE,
    PRODUCT_SETTINGS_ROUTE,
    SELECTED_ACCOUNT_KEY
} from '../../../util/constants';

import {AccessKey, AccountProgress} from '../../../util/interfaces';

import {emailStepsAreEnabled} from '../../../util/requestSender';

import {bolConnectionState, featureToggleState, shouldShowSetupSteps} from '../../../util/statusBannerUtil';

import {saveToLocal} from '../../../util/storageUtil';

import StatusBanner from './statusBanner';

/** The upload card opens the settings tab of the requests page, the same target
 *  the old dashboard's to-do used — the list itself says nothing about setup. */
const UPLOAD_SETTINGS_ROUTE = `${INVOICE_REQUESTS_ROUTE}?tab=settings`;

export default function StatusBannerController() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const [canSetupApi, canAccessEmails, canAccessInvoiceRequests, canAccessLayout, canAccessProducts] = useRight(
        AccessKey.API_SETTINGS_SETUP,
        AccessKey.PAGE_EMAIL,
        AccessKey.PAGE_INVOICE_REQUESTS,
        AccessKey.PAGE_INVOICE_LAYOUT,
        AccessKey.PAGE_SETTINGS_PRODUCTS
    );
    // The only tile whose source is not already on the account: whether the mail
    // flow has any enabled step. Same call the old dashboard card makes.
    const [emailsEnabled, setEmailsEnabled] = useState<boolean | null>(null);
    // Mirrors the account so a ticked card paints immediately instead of waiting
    // for the save to come back.
    const [setupProgress, setSetupProgress] = useState<AccountProgress | null>(null);
    const {account} = context.accountContext;
    const progress = setupProgress ?? account?.setupProgress;
    const bolConnection = useMemo(() => bolConnectionState(account?.status), [account?.status]);
    const autoUploadEnabled = account?.settings?.proactivelyUploadInvoices;
    // Same rule as the old dashboard's to-do list: open steps only stay on
    // screen while the account is new, so a seller who has run for a week
    // without ticking them is left alone.
    const showSetupCards = useMemo(
        () => shouldShowSetupSteps(progress, account?.getActiveAPIKey()?.activeSinceMoment ?? null),
        [progress, account]
    );
    // Only a connection that needs attention leads anywhere, and only for a user
    // who may run the setup — matching the old card, where the tile is inert
    // while everything is in order.
    const canFixConnection = ['orange', 'red'].includes(bolConnection) && canSetupApi && !!account;

    function onClickBolConnection() {
        if (!account) return;
        sendGaEvent('Click', 'Overview', 'API keys instellen');
        saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
        onConnectBolOauth();
    }

    function onClickAutoEmail() {
        sendGaEvent('Click', 'Overview', 'Automatisch mailen');
        navigate(EMAILS_ROUTE);
    }

    function onClickAutoUpload() {
        sendGaEvent('Click', 'Overview', 'Automatisch uploaden');
        navigate(INVOICE_REQUESTS_ROUTE);
    }

    /**
     * Unlike the old dashboard, where opening a step ticked it off on the way
     * out, these cards are only completed by the check in their corner. Opening
     * the editor and deciding the layout still needs work should not claim the
     * step is done.
     */
    function completeStep(key: keyof AccountProgress) {
        if (!account || account.setupProgress[key]) return;
        const next = {...account.setupProgress, [key]: true};
        const previous = account.setupProgress;
        setSetupProgress(next);
        account.setupProgress = next;
        account.update(undefined, undefined, undefined, (updated: Account, success: boolean) => {
            if (success) context.onChangeContext({account: updated});
            else {
                account.setupProgress = previous;
                setSetupProgress(previous);
            }
        });
    }

    /** Same shape for every card: the body opens the step, the corner check is
     *  what marks it done. Targets match the old dashboard's to-do list. */
    function openStep(label: string, route: string) {
        return function () {
            sendGaEvent('Click', 'Overview', label);
            navigate(route);
        };
    }

    function checkStep(label: string, key: keyof AccountProgress) {
        return function () {
            sendGaEvent('Click', 'Overview', `${label} afgerond`);
            completeStep(key);
        };
    }

    useEffect(() => {
        if (!account) return;
        emailStepsAreEnabled({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) setEmailsEnabled(data.are_enabled === true);
            }
        });
    }, [account?.id]);

    return (
        <>
            {/* Renders nothing: it polls GET account/status/{account_id} and writes the
                result back onto the account in context, which is what keeps the tile
                current. The old dashboard mounts it too — only one of the two pages is
                ever on screen, so they never poll at the same time. */}
            <AccountStatusChecker />
            <StatusBanner
                bolConnection={{
                    state: bolConnection,
                    onClick: canFixConnection ? onClickBolConnection : undefined
                }}
                autoEmail={{
                    state: featureToggleState(emailsEnabled),
                    onClick: canAccessEmails ? onClickAutoEmail : undefined
                }}
                autoUpload={{
                    state: featureToggleState(autoUploadEnabled),
                    onClick: canAccessInvoiceRequests ? onClickAutoUpload : undefined
                }}
                showSetupCards={showSetupCards}
                invoiceDesign={{
                    completed: progress?.pdf_layout === true,
                    onClick: canAccessLayout ? openStep('Factuur ontwerpen', PDF_LAYOUT_ROUTE) : undefined,
                    onCheckClick: checkStep('Factuur ontwerpen', 'pdf_layout')
                }}
                vatProducts={{
                    completed: progress?.vat === true,
                    onClick: canAccessProducts ? openStep('BTW producten', PRODUCT_SETTINGS_ROUTE) : undefined,
                    onCheckClick: checkStep('BTW producten', 'vat')
                }}
                emailSetup={{
                    completed: progress?.mail === true,
                    onClick: canAccessEmails ? openStep('Mails instellen', EMAILS_ROUTE) : undefined,
                    onCheckClick: checkStep('Mails instellen', 'mail')
                }}
                invoiceUpload={{
                    completed: progress?.upload === true,
                    onClick: canAccessInvoiceRequests
                        ? openStep('Auto uploaden instellen', UPLOAD_SETTINGS_ROUTE)
                        : undefined,
                    onCheckClick: checkStep('Auto uploaden instellen', 'upload')
                }}
            />
        </>
    );
}
